import React, { useState, useEffect } from 'react';
import {
  MessageCircle,
  User,
  Send,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import './TalentInquiries.css';
import { useUnread } from './TalentLayout';   // path adjust karna
import { API_BASE_URL, normalizeImageUrl } from '../../utils/api';


interface ConversationSummary {
  other_party_id: number;
  other_party_type: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
  last_message_time: string;
  unread_count: number;
  conversation_id?: string; // Unique identifier for conversation thread
}

interface TalentInquiriesProps {
  onUnreadCountChange?: (count: number) => void;
}

const TalentInquiries: React.FC = () => {  
  const { setUnreadCount } = useUnread();
  const [thread, setThread] = useState<Array<{ id: number; sender_id: number; sender_type: string; subject: string; message: string; created_at: string }>>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [compose, setCompose] = useState('');
  const [sending, setSending] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [convLoading, setConvLoading] = useState(false);
  const [selectedOtherId, setSelectedOtherId] = useState<number | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<ConversationSummary | null>(null);
  const [currentTalentId, setCurrentTalentId] = useState<number | null>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [showChatMobile, setShowChatMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);  useEffect(() => {
    const dashboardContainer = document.querySelector('.talent-dashboard');
    if (dashboardContainer) {
      dashboardContainer.classList.add('inquiries-layout-active');
    }
    return () => {
      const el = document.querySelector('.talent-dashboard');
      if (el) {
        el.classList.remove('inquiries-layout-active');
      }
    };
  }, []);
  // Report total unread count to parent (sidebar)
useEffect(() => {
  const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
  setUnreadCount(totalUnread);
  console.log('📊 Total unread from conversations:', totalUnread);

}, [conversations, setUnreadCount]);

  // Get current talent ID from token
  const getCurrentTalentId = (token: string): number | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id || null;
    } catch (e) {
      console.error('Failed to decode token:', e);
      return null;
    }
  };

  // Load recent conversations for talent
  useEffect(() => {
    const fetchTalentConversations = async () => {
      try {
        setConvLoading(true);
        const token = localStorage.getItem('skillseek_talent_token');
        if (!token) return;
        
        // Get current talent ID from token
        const talentId = getCurrentTalentId(token);
        setCurrentTalentId(talentId);
        
        const res = await fetch(`${API_BASE_URL}/messages/talent/conversations?limit=50&page=1`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const data = await res.json();
        

        console.log('Conversations data:', data);

        if (data.success) {
          // Add conversation IDs and ensure unique conversations
          const uniqueConversations = (data.conversations || []).map((conv: ConversationSummary) => ({
            ...conv,
            conversation_id: `${conv.other_party_type}_${conv.other_party_id}`
          }));
          
          console.log('Processed conversations:', uniqueConversations);
          setConversations(uniqueConversations);
          
          // Auto-select first conversation if available
          if (uniqueConversations.length > 0) {
            const firstConv = uniqueConversations[0];
            setSelectedConversation(firstConv);
            setSelectedOtherId(firstConv.other_party_id);
            openThreadForOtherId(firstConv.other_party_id, firstConv.other_party_type);
          }
        }
      } catch (e) {
        console.error('Failed to load conversations:', e);
      } finally {
        setConvLoading(false);
      }
    };
    fetchTalentConversations();
  }, []);

  const openThreadForOtherId = async (otherId: number, otherType?: string) => {
    try {
      setThreadLoading(true);
      const token = localStorage.getItem('skillseek_talent_token');
      // Use the otherType from the conversation, defaulting to 'customer' for backward compatibility
      const typeParam = otherType || 'customer';
      
      console.log('Loading thread for:', { otherId, otherType: typeParam });
      
      const res = await fetch(`${API_BASE_URL}/messages/talent/thread?otherId=${otherId}&otherType=${typeParam}&limit=50&page=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      console.log('Thread data:', data);
      
      if (data.success) {
        setSelectedOtherId(otherId);
        setThread(data.messages || []);
        console.log('Thread loaded with', data.messages?.length || 0, 'messages');
        console.log('Message sender IDs:', data.messages?.map((m: any) => ({ id: m.id, sender_id: m.sender_id, sender_type: m.sender_type })));
      }
    } catch (e) {
      console.error('Failed to load thread:', e);
      alert('Failed to load messages');
    } finally {
      setThreadLoading(false);
    }
  };

  const openConversation = async (conversation: ConversationSummary) => {
    console.log('Opening conversation:', conversation);
    
    // Clear current thread first to prevent message mixing
    setThread([]);
    setSelectedConversation(conversation);
    setSelectedOtherId(conversation.other_party_id);
    
    await openThreadForOtherId(conversation.other_party_id, conversation.other_party_type);
    setShowChatMobile(true);
  };

const refreshConversations = async () => {
  try {
    const token = localStorage.getItem('skillseek_talent_token');
    if (!token) return;
    
    const res = await fetch(`${API_BASE_URL}/messages/talent/conversations?limit=50&page=1`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();

    if (data.success) {
      const uniqueConversations = (data.conversations || []).map((conv: ConversationSummary) => ({
        ...conv,
        conversation_id: `${conv.other_party_type}_${conv.other_party_id}`
      }));
      setConversations(uniqueConversations);
    }
  } catch (e) {
    console.error('Failed to refresh conversations:', e);
  }

};

useEffect(() => {
  const interval = setInterval(() => {
    refreshConversations();
  }, 1000); 

  return () => clearInterval(interval); // cleanup
}, []);
  
  const sendTalentMessage = async () => {
    if (!compose.trim()) return;
    try {
      setSending(true);
      const token = localStorage.getItem('skillseek_talent_token');
      const recipientId = selectedOtherId;
      if (!recipientId || !selectedConversation) throw new Error('Recipient not found');
      const subject = thread.length > 0 ? thread[thread.length - 1].subject : 'Direct Message';
      const recipientType = selectedConversation.other_party_type;
      const res = await fetch(`${API_BASE_URL}/messages/talent/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          recipientId, 
          recipientType, 
          subject: subject.startsWith('Re:') ? subject : `Re: ${subject}`, 
          message: compose 
        })
      });
      const data = await res.json();
      if (data.success) {
        setThread(prev => ([...prev, { id: data.messageId || Date.now(), sender_id: currentTalentId || 0, sender_type: 'talent', subject, message: compose, created_at: new Date().toISOString() }]));
        setCompose('');
        // Refresh conversations
        try {
          setConvLoading(true);
          const listRes = await fetch(`${API_BASE_URL}/messages/talent/conversations?limit=50&page=1`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const listData = await listRes.json();
          if (listData.success) {
            const uniqueConversations = (listData.conversations || []).map((conv: ConversationSummary) => ({
              ...conv,
              conversation_id: `${conv.other_party_type}_${conv.other_party_id}`
            }));
            setConversations(uniqueConversations);
          }
        } finally {
          setConvLoading(false);
        }
      } else {
        alert('Failed to send message');
      }
    } catch (e) {
      console.error('Send message failed:', e);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendTalentMessage();
    }
  };

  return (
    <div className={`talent-inquiries ${isMobile ? 'mobile-view' : ''} ${showChatMobile ? 'chat-active' : 'list-active'}`}>
      <div className="conversations-container">
        {/* Left Column - Customer List */}
        <div className="customers-column">
          <div className="customers-header">
                          <div className="header-content">
                <div>
                  <h2>Conversations ({conversations.length})</h2>
                  <p>Select a customer or fellow talent to start chatting</p>
                </div>
                <button 
                  className="refresh-button" 
                  onClick={refreshConversations}
                  disabled={convLoading}
                  title="Refresh conversations"
                >
                  <RefreshCw size={16} className={convLoading ? 'spinning' : ''} />
                </button>
              </div>
          </div>

          {convLoading ? (
            <div className="loading-spinner" />
          ) : conversations.length === 0 ? (
            <div className="no-conversations">
              <MessageCircle size={48} />
              <h3>No conversations yet</h3>
              <p>You haven't had any conversations yet.</p>
            </div>
          ) : (
            <>
             
              <div className="customers-list">
                {conversations.map((conversation) => (
                  <div
                    key={`${conversation.other_party_type}_${conversation.other_party_id}`}
className={`customer-item ${selectedConversation?.other_party_id === conversation.other_party_id ? 'active' : ''} ${conversation.other_party_type === 'talent' ? 'talent-conversation-item' : 'customer-conversation-item'}`}                    onClick={() => openConversation(conversation)}
                  >
                    <div className="customer-avatar">
                      {conversation.profile_picture ? (
                        <img src={normalizeImageUrl(conversation.profile_picture)} alt="User" />
                      ) : (
                        <User size={24} />
                      )}
                      {conversation.other_party_type === 'talent' && (
                        <div className="talent-indicator">👥</div>
                      )}
                      {conversation.unread_count > 0 && (
                        <span className="unread-indicator">{conversation.unread_count}</span>
                      )}
                    </div>
                    
                    <div className="customer-info">
                      <div className="customer-name">
                        <h4>{conversation.first_name} {conversation.last_name}</h4>
                        {conversation.unread_count > 0 && (
                          <span className="unread-dot"></span>
                        )}
                      </div>
                      <p className="conversation-type">
                        {conversation.other_party_type === 'talent' ? '👥 Fellow Talent' : '👤 Customer'}
                      </p>
                      <p className="last-message-time">
                        {new Date(conversation.last_message_time).toLocaleDateString()}
                      </p>
                      <p className="conversation-id">
                        ID: {conversation.conversation_id}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right Column - Chat Messages */}
        <div className="chat-column">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className={`chat-header ${selectedConversation.other_party_type === 'talent' ? 'talent-chat' : 'customer-chat'}`}>
                <div className="chat-user-info">
                  {isMobile && (
                    <button 
                      className="chat-back-button"
                      onClick={() => setShowChatMobile(false)}
                      title="Back to conversations"
                    >
                      <ArrowLeft size={20} />
                    </button>
                  )}
                  <div className="chat-user-avatar">
                    {selectedConversation.profile_picture ? (
                      <img src={normalizeImageUrl(selectedConversation.profile_picture)} alt="User" />
                    ) : (
                      <User size={24} />
                    )}
                    {selectedConversation.other_party_type === 'talent' && (
                      <div className="talent-badge">👥</div>
                    )}
                  </div>
                  <div className="chat-user-details">
                    <h3>{selectedConversation.first_name} {selectedConversation.last_name}</h3>
                    <p className={`chat-user-type ${selectedConversation.other_party_type === 'talent' ? 'talent-type' : 'customer-type'}`}>
                      {selectedConversation.other_party_type === 'talent' ? '👥 Fellow Talent' : '👤 Customer'}
                    </p>
                    <p className="message-count">
                      {thread.length} message{thread.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div className={`chat-messages ${selectedConversation.other_party_type === 'talent' ? 'talent-messages' : 'customer-messages'}`}>
                {threadLoading ? (
                  <div className="loading-spinner" />
                ) : thread.length === 0 ? (
                  <div className="no-messages">
                    <MessageCircle size={48} />
                    <h3>No messages yet</h3>
                    <p>Start the conversation by sending a message</p>
                  </div>
                ) : (
                  <div className="messages-container">
                    {thread.map(m => {
                      const isCurrentTalentMessage = m.sender_id === currentTalentId;
                      return (
                        <div key={m.id} className={`message-bubble ${isCurrentTalentMessage ? 'sent' : 'received'}`}>
                          <div className="message-content">
                            <div className="message-sender">
                              {isCurrentTalentMessage ? 'You' : `${selectedConversation.first_name}`}
                            </div>
                            <div className="message-text">{m.message}</div>
                            <div className="message-time">
                              {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="chat-input-container">
                <div className="chat-input-wrapper">
                  <input 
                    className="chat-input"
                    value={compose} 
                    onChange={(e) => setCompose(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                  />
                  <button 
                    className="send-button"
                    onClick={sendTalentMessage} 
                    disabled={sending || !compose.trim()}
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <MessageCircle size={64} />
              <h3>Select a conversation</h3>
              <p>Choose a customer or fellow talent from the list to start chatting</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TalentInquiries;


